/**
 * HTML sanitization for CMS page bodies.
 *
 * Sanitization runs on the CLIENT (in the browser) before the rich-text
 * body is injected via `dangerouslySetInnerHTML`. Scripts, event handlers,
 * and other dangerous markup are stripped; a safe subset of tags and
 * attributes (enough to render readable CMS content) is allowed.
 *
 * A DOM-based sanitizer is used (rather than a regex) so the browser parser
 * normalizes the markup first, which is far more reliable than string
 * matching. On the server, where no DOM is available, an empty string is
 * returned — the client fills in the sanitized content after hydration.
 */

const ALLOWED_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "img",
  "blockquote",
  "br",
  "hr",
  "span",
  "div",
  "figure",
  "figcaption",
  "code",
  "pre",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
]);

const ALLOWED_ATTR = new Set([
  "class",
  "href",
  "src",
  "alt",
  "title",
  "target",
  "rel",
  "width",
  "height",
]);

// Elements that must be removed *with* their contents (never unwrapped).
const DROP_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "noscript",
]);

function sanitizeNode(node: Element): void {
  const children = Array.from(node.children);
  for (const child of children) {
    const tag = child.tagName.toLowerCase();

    if (DROP_TAGS.has(tag)) {
      child.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap unknown elements: replace the element with its children so
      // their text content is preserved.
      const parent = child.parentNode;
      if (parent) {
        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
      }
      continue;
    }

    // Strip disallowed attributes and inline event handlers (on*).
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value;
      if (name.startsWith("on") || !ALLOWED_ATTR.has(name)) {
        child.removeAttribute(attr.name);
        continue;
      }
      // Block javascript:/data: URLs in link/image targets.
      if (
        (name === "href" || name === "src") &&
        /^\s*(javascript|vbscript|data):/i.test(value)
      ) {
        child.removeAttribute(attr.name);
      }
    }

    sanitizeNode(child);
  }
}

export function sanitizeHtml(dirty: string): string {
  if (typeof document === "undefined") return "";
  const container = document.createElement("div");
  container.innerHTML = dirty ?? "";
  sanitizeNode(container);
  return container.innerHTML;
}
